import SentryHelper from '../sentry/_sentry-helper';

enum ResponseType {
    empty = 'empty',
    modalMessage = 'modalMessage',
    inlineMessage = 'inlineMessage',
    markup = 'markup',
    json = 'json'
}

export interface AjaxResponse {
    status: number,
    statusDescription: string,
    type: ResponseType,
    content?: string,
    contentElementId?: string,
    modalHeader?: string,
    modalRedirectUrl?: string,
    alertClass?: string,
}

let hasOngoingAjaxRequest = false;

function loadAjax(): void {
    $(document).ajaxStart(function () {
        hasOngoingAjaxRequest = true;
    });
    $(document).ajaxStop(function () {
        hasOngoingAjaxRequest = false;
    });
    $(function () {
        // Submits the form only after all ajax requests are stopped.
        $('button[type="submit"]', 'form').on('click', function (e) {
            if (hasOngoingAjaxRequest) {
                e.preventDefault(); // Prevent the form being submitted
                const submitButton = <HTMLElement>this;
                ajaxRequestsEnded().then(function () {
                    // Submit the form by triggering the click event again
                    submitButton.click();
                });
            }
        });
    });
}

// Show message modal with optional header
function showModalMessage(messageContent?: string, alertClass?: string, headerContent?: string): void {
    const $contentElement = $('#sl-js-message-content');

    // alertClass should be 'alert-success', 'alert-info', or 'alert-danger'
    $contentElement.addClass(alertClass);
    $contentElement.html(messageContent);

    if (typeof headerContent !== "undefined") {
        const $headerElement = $('#sl-js-message-modal-header');
        $headerElement.html(headerContent);
    }

    // Hide all (other) modals
    $('.modal').modal('hide');
    // Show ajax modal
    $('#sl-js-message-modal').modal('show');
}

/**
 * Performs an ajax request (POST by default).
 *
 * The default settings may be overriden in the passed ajaxSettings.
 * This function handles
 * - request timeouts
 * - displaying of error messages in a modal
 * - displaying markup at a given DOM element
 *
 * @param ajaxSettings Must contain an url and data attribute
 * @param maxRetry Specifies how often the ajax call tries again if it fails with error code '0'.
 * @param retryCount The current count of tries/attempts in relation to the parameter 'maxRetry'.
 * @returns Returns the jqXHR object which may be used to attach more promises.
 */
function doAjax(ajaxSettings: object, maxRetry?: number, retryCount?: number): JQuery.jqXHR {
    maxRetry = typeof maxRetry !== 'undefined' ? maxRetry : 5; // Allow 5
    retryCount = typeof retryCount !== 'undefined' ? retryCount : 1;
    const defaultSettings = {
        type: "POST",
        dataType: "json",
        global: true,
        timeout: 8000
    };
    const mergedSettings = $.extend({}, defaultSettings, ajaxSettings);

    const jqXHR = $.ajax(mergedSettings);

    jqXHR.fail(function (jqXHR: JQuery.jqXHR, textStatus: JQuery.Ajax.ErrorTextStatus) {
        // If the ajax call fails with status code '0', try the ajax call again.
        // This is a workaround because this error happens sometimes e.g. in IE10 & Safari.
        if (jqXHR.status === 0) {
            if (retryCount < maxRetry) {
                doAjax(ajaxSettings, maxRetry, retryCount + 1);
            } else {
                SentryHelper.log(
                    'jqXHR.status was still 0 after trying ' + maxRetry + ' times, ajax request could not be processed.',
                    SentryHelper.getSentry().Severity.Info,
                    jqXHR
                );
            }
        } else {
            processAjaxFail(jqXHR, textStatus)
        }
    });

    jqXHR.done(function (response: AjaxResponse) {
        if (responseContainsMessage(response)) {
            processResponseMessage(response);
        }
    });

    // The arguments of .always() depend on whether the request was a succes or a failure which makes it hard to use.

    return jqXHR;
}

function responseContainsMessage(response: AjaxResponse): boolean {
    return response.type === ResponseType.modalMessage || response.type === ResponseType.inlineMessage || response.type === ResponseType.markup;
}

/**
 * Check if response is a modal or inline message and display it.
 *
 * @returns Returns true if a message is displayed, otherwise false.
 */
function processResponseMessage(response: AjaxResponse): boolean {
    if (response.type === ResponseType.modalMessage) {
        showModalMessage(response.content, response.alertClass, response.modalHeader);
        if (typeof response.modalRedirectUrl !== 'undefined') {
            // Redirect when message is closed
            $('#sl-js-message-modal').on('hidden.bs.modal', function () {
                // Prevent confirm-browse-away message (see confirm-browse-away.js)
                (<any>window).showConfirmBrowseAway = false;
                window.location.href = response.modalRedirectUrl;
            });
        }
        return true;
    } else if (response.type === ResponseType.inlineMessage || response.type === ResponseType.markup) {
        showAjaxResponseContent(response);
        return true;
    }
    return false;
}

/**
 * Display error messages for ajax fail promise.
 */
function processAjaxFail(jqXHR: JQuery.jqXHR, textStatus: JQuery.Ajax.ErrorTextStatus): void {
    if (textStatus === "timeout") {
        showModalMessage(<string>$('#sl-js-timeout-message').val(), 'alert-danger', <string>$('#sl-js-timeout-header').val());
        return;
    }

    let response = undefined;
    try {
        response = JSON.parse(jqXHR.responseText);
    } catch (e) {
        // Ignore any errors, jqXHR.responseText may be undefined or contain an invalid JSON string.
    }
    if (response === undefined || processResponseMessage(response) === false) {
        // Generic error message
        SentryHelper.log(
            'An unexpected ajax failure occurred.',
            SentryHelper.getSentry().Severity.Error,
            jqXHR
        );
        showModalMessage(<string>$('#sl-js-default-error-message').val(), 'alert-danger', <string>$('#sl-js-default-error-header').val());
    }
}

/**
 * Displays inline message or markup from a response at a given DOM element.
 */
function showAjaxResponseContent(response: AjaxResponse): void {
    //noinspection JSUnresolvedVariable
    const $contentElement = $(response.contentElementId);
    if ('alertClass' in response) {
        //noinspection JSUnresolvedVariable
        $contentElement.addClass(response.alertClass);
    }
    $contentElement.html(response.content);
    $contentElement.show();
}

/**
 * Returns a deferred object that gets resolved when all ajax requests ended.
 */
function ajaxRequestsEnded(): JQuery.Deferred<any> {
    const def = $.Deferred();
    if (hasOngoingAjaxRequest) {
        $(document).ajaxStop(function () {
            def.resolve();
        });
    } else {
        def.resolve();
    }
    return def;
}

export {loadAjax, doAjax, showAjaxResponseContent};